import React, { useState, useMemo, useEffect, useCallback } from'react';
import { useSearchParams } from'react-router-dom';
import { X, Copy, Check } from'lucide-react';
import { useLayout } from'../components/Layout';
import { useApiQuery } from'../hooks/useApi';
import api from'../api/client';
import DataTable from'../components/tables/DataTable';
import AreaChart from'../components/charts/AreaChart';
import BarChart from'../components/charts/BarChart';
import HeatmapChart from'../components/charts/HeatmapChart';
import Breadcrumbs from'../components/Breadcrumbs';
import ErrorBoundary from'../components/ErrorBoundary';
import PageLoader, { InlineLoader } from'../components/PageLoader';

// Maps the `?sort=` deep-link token to a table column + direction. Keeps the
// URL contract readable ("/apis?sort=errorRate") and decoupled from the
// internal field names.
const SORT_PRESETS = {
 errorRate: { key:'errorRate', dir:'desc' },
 calls: { key:'totalCalls', dir:'desc' },
 latency: { key:'p95Latency', dir:'desc' },
 rejected: { key:'edgeRejections', dir:'desc' },
};

// The three analysis lenses this page offers. "apis" is the original per-API
// view; the other two add the source/application-level drill-down requested
// in the "errors & usage by product/application" user story.
const VIEWS = [
 { key:'apis', label:'By API' },
 { key:'product', label:'By Product' },
 { key:'app', label:'By Application' },
];

export default function ApiAnalytics() {
 const [searchParams, setSearchParams] = useSearchParams();
 // The active lens is URL-driven (?view=product) so it's deep-linkable and
 // survives refreshes, consistent with the existing ?sort / ?status params.
 const viewParam = searchParams.get('view');
 const view = VIEWS.some((v) => v.key === viewParam) ? viewParam : 'apis';

 const setView = useCallback((next) => {
 setSearchParams((prev) => {
 const p = new URLSearchParams(prev);
 if (next === 'apis') p.delete('view');
 else p.set('view', next);
 return p;
 });
 }, [setSearchParams]);

 return (
 <div className="space-y-6">
 <Breadcrumbs />
 <div>
 <h1 className="text-xl font-bold">API Analytics</h1>
 <p className="text-sm text-gray-500 mt-0.5">
 Per-API performance, plus errors &amp; usage with product / application drill-down
 </p>
 </div>

 {/* View switcher — segmented control. Horizontally scrollable on very
     narrow screens so the three lenses never wrap or clip. */}
 <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-full sm:w-auto sm:inline-flex overflow-x-auto">
 {VIEWS.map((v) => (
 <button
 key={v.key}
 onClick={() => setView(v.key)}
 className={`flex-1 sm:flex-none whitespace-nowrap px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
 view === v.key
 ? 'bg-white text-brand-600 shadow-sm'
 : 'text-gray-500 hover:text-gray-700'
 }`}
 aria-pressed={view === v.key}
 >
 {v.label}
 </button>
 ))}
 </div>

 {view === 'apis' ? <ApisView /> : <ConsumerErrorsView dimension={view} />}
 </div>
 );
}

function ApisView() {
 const { timeRange, granularity } = useLayout();
 const [selectedApi, setSelectedApi] = useState(null);
 // When set, opens the failing-calls drill-down modal for the selected API —
 // the same modal the By Product / By Application lenses use, so the three
 // views offer a consistent "inspect the actual errors" affordance.
 const [errorsApi, setErrorsApi] = useState(null); // { id, name }
 const [searchParams] = useSearchParams();

 // Deep-link support: the Dashboard KPI cards navigate here with a sort
 // (and optionally a filter) so the user lands on the rows they clicked
 // to investigate, rather than an unordered list they must re-sort.
 const sortParam = searchParams.get('sort');
 const statusParam = searchParams.get('status'); //'failing' — APIs with errors;'rejected' — APIs with edge rejections
 // When deep-linked with ?status=rejected (from the Edge Rejections KPI) but
 // no explicit ?sort, default to sorting by edge rejections so the offending
 // APIs surface at the top.
 const initialSort = SORT_PRESETS[sortParam]
 || (statusParam ==='rejected' ? SORT_PRESETS.rejected : null);

 const { data: apis, isPending: apisPending } = useApiQuery(
 ['apis', timeRange],
 () => api.getApis(timeRange),
 { refetchInterval: 60000 }
 );

 const { data: timeSeries, isPending: timeSeriesPending } = useApiQuery(
 ['api-timeseries', selectedApi, timeRange, granularity],
 () => api.getApiTimeSeries(selectedApi, timeRange, granularity),
 { enabled: !!selectedApi, refetchInterval: 30000 }
 );

 const { data: heatmap } = useApiQuery(
 ['api-heatmap', selectedApi],
 () => api.getApiHeatmap(selectedApi,'7d'),
 { enabled: !!selectedApi }
 );

 const { data: errors } = useApiQuery(
 ['api-errors', selectedApi, timeRange],
 () => api.getApiErrors(selectedApi, timeRange),
 { enabled: !!selectedApi }
 );

 // When deep-linked with `?status=failing` (from the Error Rate KPI) show
 // only APIs that actually have errors, so the user isn't scanning past
 // healthy rows to find the problem.
 const visibleApis = useMemo(() => {
 const all = apis || [];
 if (statusParam ==='failing') {
 return all.filter((a) => (a.errorCount || 0) > 0);
 }
 if (statusParam ==='rejected') {
 return all.filter((a) => (a.edgeRejections || 0) > 0);
 }
 return all;
 }, [apis, statusParam]);

 // Lightweight reconciling totals so this screen ties out to the Executive
 // Dashboard at a glance (served Calls sum, total Errors, total Edge
 // Rejections). Computed over the CURRENTLY VISIBLE rows so the strip always
 // describes what the table shows.
 const totals = useMemo(() => {
 return (visibleApis || []).reduce(
 (acc, a) => {
 acc.calls += a.totalCalls || 0;
 acc.errors += a.errorCount || 0;
 acc.rejections += a.edgeRejections || 0;
 return acc;
 },
 { calls: 0, errors: 0, rejections: 0 }
 );
 }, [visibleApis]);

 const columns = [
 { headerName:'API', field:'displayName', flex: 2,
 cellRenderer: (p) => (
 <span className="font-medium text-brand-600 cursor-pointer hover:underline">
 {p.value}
 </span>
 ),
 },
 { headerName:'Path', field:'path', flex: 1 },
 { headerName:'Calls', field:'totalCalls', width: 100, type:'numericColumn',
 valueFormatter: (p) => (p.value || 0).toLocaleString() },
 { headerName:'Avg Latency', field:'avgLatency', width: 110, type:'numericColumn',
 valueFormatter: (p) =>`${p.value || 0}ms` },
 { headerName:'P95', field:'p95Latency', width: 90, type:'numericColumn',
 valueFormatter: (p) =>`${p.value || 0}ms` },  { headerName:'Errors', field:'errorCount', width: 80, type:'numericColumn' },
 { headerName:'Error %', field:'errorRate', width: 90, type:'numericColumn',
 valueFormatter: (p) =>`${(p.value || 0).toFixed(1)}%`,
 cellStyle: (p) => p.value > 5 ? { color:'#ef4444' } : {},
 },
 { headerName:'Edge Rejections', field:'edgeRejections', width: 130, type:'numericColumn',
 headerTooltip:'Requests refused at the gateway (401/403/429 — bad token, forbidden, or rate-limited). Not counted as calls or errors.',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 cellStyle: (p) => p.value > 0 ? { color:'#d97706' } : {},
 },
 ];

 return (
 <div className="space-y-6">
 {(statusParam ==='failing' || statusParam ==='rejected') && (
 <div>
 {statusParam ==='failing' && (
 <p className="text-sm text-red-600">
 Showing only APIs with errors — sorted by error rate.
 </p>
 )}
 {statusParam ==='rejected' && (
 <p className="text-sm text-amber-600">
 Showing only APIs with edge rejections (401/403/429) — sorted by rejection count.
 </p>
 )}
 </div>
 )}

 {/* Reconciling summary strip — ties out to the Executive Dashboard:
     served Calls, failed calls (Errors), and Edge Rejections use the
     same served-traffic definitions as the dashboard headline. */}
 {!apisPending && (
 <div className="grid grid-cols-3 gap-3 sm:gap-4">
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Served Calls
 </div>
 <div className="mt-1 text-lg font-bold tabular-nums text-gray-900">
 {totals.calls.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">across {visibleApis.length} APIs</div>
 </div>
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Errors
 </div>
 <div className={`mt-1 text-lg font-bold tabular-nums ${totals.errors > 0 ?'text-red-600' :'text-gray-900'}`}>
 {totals.errors.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">
 {totals.calls > 0 ? `${((totals.errors / totals.calls) * 100).toFixed(1)}% of served` :'—'}
 </div>
 </div>
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Edge Rejections
 </div>
 <div className={`mt-1 text-lg font-bold tabular-nums ${totals.rejections > 0 ?'text-amber-600' :'text-gray-900'}`}>
 {totals.rejections.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">blocked at gateway (401/403/429)</div>
 </div>
 </div>
 )}

 <ErrorBoundary>
 {apisPending ? (
 <PageLoader message="Loading APIs…" />
 ) : (
 <DataTable
 data={visibleApis}
 columns={columns}
 title={`${visibleApis.length} APIs`}
 height={350}
 initialSort={initialSort}
 exportFilename="api-analytics"
 onRowClick={(row) => setSelectedApi(row.id || row.displayName)}
 />
 )}
 </ErrorBoundary>

 {selectedApi && (
 <>
 <div className="pt-2 border-t border-gray-200 flex items-center justify-between gap-3 flex-wrap">
 <h2 className="text-lg font-semibold">{selectedApi}</h2>
 <button
 type="button"
 onClick={() => {
 const match = (apis || []).find((a) => (a.id || a.displayName) === selectedApi);
 setErrorsApi({ id: selectedApi, name: match?.displayName || selectedApi });
 }}
 className="text-sm font-medium text-brand-600 hover:underline whitespace-nowrap"
 >
 View failing calls →
 </button>
 </div>

 {timeSeriesPending ? (
 <InlineLoader message="Loading API analytics…" />
 ) : (
 <>
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ErrorBoundary>
 <AreaChart
 data={timeSeries || []}
 dataKeys={['Calls','Errors']}
 title="Calls & Errors Over Time"
 exportFilename={`api-${selectedApi}-calls`}
 />
 </ErrorBoundary>
 <ErrorBoundary>
 <AreaChart
 data={timeSeries || []}
 dataKeys={['AvgLatency','P95Latency']}
 title="Latency Over Time (ms)"
 exportFilename={`api-${selectedApi}-latency`}
 />
 </ErrorBoundary>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
 <ErrorBoundary>
 <HeatmapChart
 data={heatmap || []}
 title="Usage Heatmap (Last 7 Days)"
 />
 </ErrorBoundary>
 {errors && errors.length > 0 && (
 <ErrorBoundary>
 <BarChart                data={errors.map((e) => ({ name:`HTTP ${e.ResponseCode}`, Count: parseInt(e.Count, 10) }))}
 dataKeys={['Count']}
 xAxisKey="name"
 title="Error Breakdown"
 colorByIndex
 height={250}
 exportFilename={`api-${selectedApi}-errors`}
 />
 </ErrorBoundary>
 )}
 </div>
 </>
 )}
 </>
 )}

 {/* Failing-calls drill-down — shares the ErrorEventsModal with the
     By Product / By Application lenses (dimension="api"), so the deep
     "inspect the actual errors + gateway call log" flow is consistent
     across all three views. */}
 {errorsApi && (
 <ErrorEventsModal
 dimension="api"
 value={errorsApi.id}
 name={errorsApi.name}
 timeRange={timeRange}
 onClose={() => setErrorsApi(null)}
 />
 )}
 </div>
 );
}

/**
 * Shared summary view for both consumer dimensions. `dimension` is 'product'
 * or 'app'; the data shape only differs by the id/name field names, which we
 * normalise here so one component serves both lenses (no duplication).
 */
function ConsumerErrorsView({ dimension }) {
 const { timeRange } = useLayout();
 const isProduct = dimension === 'product';

 const { data, isPending } = useApiQuery(
 ['consumer-errors', dimension, timeRange],
 () => (isProduct ? api.getErrorsByProduct(timeRange) : api.getErrorsByApp(timeRange)),
 { refetchInterval: 60000 }
 );

 // The drill-down target: { id, name } of the product/app whose failing
 // calls we're inspecting. null = modal closed.
 const [selected, setSelected] = useState(null);

 // Normalise rows so the table + totals are dimension-agnostic.
 const rows = useMemo(() => {
 const items = (data && data.items) || [];
 return items.map((r) => ({
 id: isProduct ? r.productId : r.appClientId,
 name: isProduct ? r.productName : r.appName,
 totalCalls: r.totalCalls || 0,
 errorCount: r.errorCount || 0,
 errorRate: r.errorRate || 0,
 throttledCalls: r.throttledCalls || 0,
 edgeRejections: r.edgeRejections || 0, // product-only; undefined→0 for app
 avgLatency: r.avgLatency || 0,
 p95Latency: r.p95Latency || 0,
 }));
 }, [data, isProduct]);

 const totals = useMemo(() => rows.reduce(
 (acc, r) => {
 acc.calls += r.totalCalls;
 acc.errors += r.errorCount;
 acc.throttled += r.throttledCalls;
 return acc;
 },
 { calls: 0, errors: 0, throttled: 0 }
 ), [rows]);

 const label = isProduct ? 'Product' : 'Application';

 const columns = useMemo(() => {
 const cols = [
 { headerName: label, field:'name', flex: 2,
 cellRenderer: (p) => (
 <span className="font-medium text-brand-600 cursor-pointer hover:underline">
 {p.value}
 </span>
 ),
 },
 { headerName:'Calls', field:'totalCalls', width: 100, type:'numericColumn',
 valueFormatter: (p) => (p.value || 0).toLocaleString() },
 { headerName:'Errors', field:'errorCount', width: 90, type:'numericColumn',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 cellStyle: (p) => p.value > 0 ? { color:'#ef4444' } : {},
 },
 { headerName:'Error %', field:'errorRate', width: 95, type:'numericColumn',
 valueFormatter: (p) =>`${(p.value || 0).toFixed(1)}%`,
 cellStyle: (p) => p.value > 5 ? { color:'#ef4444' } : {},
 },
 { headerName:'Throttled', field:'throttledCalls', width: 110, type:'numericColumn',
 headerTooltip:'Requests rate-limited at the gateway (HTTP 429).',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 cellStyle: (p) => p.value > 0 ? { color:'#d97706' } : {},
 },
 ];
 if (isProduct) {
 cols.push({ headerName:'Edge Rejections', field:'edgeRejections', width: 130, type:'numericColumn',
 headerTooltip:'Requests refused at the gateway (401/403/429). Not counted as calls or errors.',
 valueFormatter: (p) => (p.value || 0).toLocaleString(),
 cellStyle: (p) => p.value > 0 ? { color:'#d97706' } : {},
 });
 }
 cols.push({ headerName:'P95', field:'p95Latency', width: 90, type:'numericColumn',
 valueFormatter: (p) =>`${p.value || 0}ms` });
 return cols;
 }, [isProduct, label]);

 return (
 <div className="space-y-6">
 {/* App-dimension footnote: app-level counts only LLM calls (the only
     traffic carrying an appClientId), so its totals legitimately differ
     from the product view. State it plainly so it's not read as a bug. */}
 {!isProduct && (
 <p className="text-xs text-gray-400">
 Application-level figures count LLM calls only (traffic carrying a
 caller app identity); totals may differ from the product view.
 </p>
 )}

 {!isPending && (
 <div className="grid grid-cols-3 gap-3 sm:gap-4">
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Calls
 </div>
 <div className="mt-1 text-lg font-bold tabular-nums text-gray-900">
 {totals.calls.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">across {rows.length} {isProduct ?'products' :'apps'}</div>
 </div>
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Errors
 </div>
 <div className={`mt-1 text-lg font-bold tabular-nums ${totals.errors > 0 ?'text-red-600' :'text-gray-900'}`}>
 {totals.errors.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">
 {totals.calls > 0 ? `${((totals.errors / totals.calls) * 100).toFixed(1)}% of calls` :'—'}
 </div>
 </div>
 <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
 Throttled
 </div>
 <div className={`mt-1 text-lg font-bold tabular-nums ${totals.throttled > 0 ?'text-amber-600' :'text-gray-900'}`}>
 {totals.throttled.toLocaleString()}
 </div>
 <div className="text-[11px] text-gray-400">rate-limited (429)</div>
 </div>
 </div>
 )}

 <ErrorBoundary>
 {isPending ? (
 <PageLoader message={`Loading ${label.toLowerCase()} errors…`} />
 ) : (
 <DataTable
 data={rows}
 columns={columns}
 title={`${rows.length} ${isProduct ?'Products' :'Applications'}`}
 height={420}
 initialSort={{ key:'errorCount', dir:'desc' }}
 exportFilename={`errors-by-${dimension}`}
 emptyMessage={`No ${label.toLowerCase()} traffic in this window.`}
 onRowClick={(row) => row.errorCount > 0 && setSelected({ id: row.id, name: row.name })}
 />
 )}
 </ErrorBoundary>

 {!isPending && (
 <p className="text-xs text-gray-400">
 Tip: click a row with errors to view its failing calls.
 </p>
 )}

 {selected && (
 <ErrorEventsModal
 dimension={dimension}
 value={selected.id}
 name={selected.name}
 timeRange={timeRange}
 onClose={() => setSelected(null)}
 />
 )}
 </div>
 );
}

/**
 * Drill-down modal: the actual failing calls for one product/application, with
 * a secondary drill into the full gateway log for a single call. Mirrors the
 * app's existing modal pattern (see KillSwitch): fixed overlay, header with a
 * close affordance, scrollable body, pinned on mobile via dvh sizing.
 */
function ErrorEventsModal({ dimension, value, name, timeRange, onClose }) {
 const [call, setCall] = useState(null); // selected CorrelationId for log view

 // Close on Escape — matches the rest of the app's modals.
 useEffect(() => {
 const onKey = (e) => { if (e.key === 'Escape') (call ? setCall(null) : onClose()); };
 window.addEventListener('keydown', onKey);
 return () => window.removeEventListener('keydown', onKey);
 }, [onClose, call]);

 const { data, isPending } = useApiQuery(
 ['error-events', dimension, value, timeRange],
 () => api.getErrorEvents(dimension, value, timeRange, 200),
 { enabled: !!value }
 );

 const events = (data && data.items) || [];

 return (
 <div
 className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
 role="presentation"
 onClick={onClose}
 >
 <div
 role="dialog"
 aria-modal="true"
 aria-label={`Failing calls for ${name}`}
 className="bg-white w-full sm:max-w-4xl rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92dvh] sm:max-h-[90dvh] flex flex-col overflow-hidden"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-gray-200 shrink-0">
 <div className="min-w-0 flex-1">
 <h2 className="text-lg font-bold text-gray-900 truncate">{name}</h2>
 <p className="text-sm text-gray-500">
 Failing calls{data ? ` · ${events.length}` :''} · {timeRange}
 </p>
 </div>
 <button
 onClick={onClose}
 className="p-1 text-gray-400 hover:text-gray-600"
 aria-label="Close dialog"
 >
 <X className="w-5 h-5" />
 </button>
 </div>

 <div className="p-4 sm:p-5 overflow-y-auto flex-1">
 {isPending ? (
 <InlineLoader message="Loading failing calls…" />
 ) : events.length === 0 ? (
 <p className="text-sm text-gray-500">No failing calls found in this window.</p>
 ) : (
 // Horizontal scroll wrapper keeps the table usable on small screens
 // without squashing columns.
 <div className="overflow-x-auto -mx-1">
 <table className="min-w-full text-sm">
 <thead>
 <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-200">
 <th className="px-2 py-2">Time</th>
 <th className="px-2 py-2">API</th>
 <th className="px-2 py-2 text-right">Status</th>
 <th className="px-2 py-2">Reason</th>
 <th className="px-2 py-2 text-right">Latency</th>
 <th className="px-2 py-2"></th>
 </tr>
 </thead>
 <tbody className="divide-y divide-gray-100">
 {events.map((e) => (
 <tr key={e.CorrelationId} className="hover:bg-gray-50">
 <td className="px-2 py-2 whitespace-nowrap text-gray-600 tabular-nums">
 {new Date(e.TimeGenerated).toLocaleString()}
 </td>
 <td className="px-2 py-2 font-medium text-gray-900">{e.ApiId}</td>
 <td className="px-2 py-2 text-right">
 <span className="inline-block rounded px-1.5 py-0.5 text-xs font-semibold bg-red-50 text-red-700 tabular-nums">
 {e.ResponseCode}
 </span>
 </td>
 <td className="px-2 py-2 text-gray-600 max-w-[220px] truncate" title={e.LastErrorReason || ''}>
 {e.LastErrorReason || '—'}
 </td>
 <td className="px-2 py-2 text-right tabular-nums text-gray-600">
 {e.TotalTime || 0}ms
 </td>
 <td className="px-2 py-2 text-right">
 <button
 onClick={() => setCall(e.CorrelationId)}
 className="text-xs font-medium text-brand-600 hover:underline whitespace-nowrap"
 >
 View log
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>

 {call && (
 <CallLogPanel correlationId={call} onClose={() => setCall(null)} />
 )}
 </div>
 </div>
 );
}

/**
 * Small copy-to-clipboard affordance. Shows a transient check on success so
 * the user gets feedback without a toast system. Used for the correlation id
 * and the raw request/response payloads in the call-log panel.
 */
function CopyButton({ text, label = 'Copy', className = '' }) {
 const [copied, setCopied] = useState(false);
 const onCopy = useCallback(async () => {
 try {
 await navigator.clipboard.writeText(
 typeof text === 'string' ? text : JSON.stringify(text, null, 2)
 );
 setCopied(true);
 setTimeout(() => setCopied(false), 1500);
 } catch {
 /* clipboard unavailable (insecure context) — silently ignore */
 }
 }, [text]);
 return (
 <button
 type="button"
 onClick={onCopy}
 className={`inline-flex items-center gap-1 text-xs font-medium ${copied ? 'text-green-600' : 'text-gray-400 hover:text-gray-600'} ${className}`}
 aria-label={copied ? 'Copied' : label}
 >
 {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
 {copied ? 'Copied' : label}
 </button>
 );
}

/**
 * Innermost drill: the full gateway-log row for one call. Rendered as a
 * nested overlay on top of the events modal.
 */
function CallLogPanel({ correlationId, onClose }) {
 const { data, isPending } = useApiQuery(
 ['error-call', correlationId],
 () => api.getErrorCall(correlationId,'30d'),
 { enabled: !!correlationId }
 );

 const Field = ({ label, value }) => (
 <div className="min-w-0">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
 <div className="text-sm text-gray-800 break-words">{value ?? '—'}</div>
 </div>
 );

 return (
 <div
 className="absolute inset-0 z-10 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
 role="presentation"
 onClick={onClose}
 >
 <div
 role="dialog"
 aria-modal="true"
 aria-label="Gateway call log"
 className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[88dvh] flex flex-col overflow-hidden"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="flex items-center gap-3 p-4 border-b border-gray-200 shrink-0">
 <h3 className="text-base font-bold text-gray-900 flex-1">Gateway call log</h3>
 <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
 <X className="w-5 h-5" />
 </button>
 </div>
 <div className="p-4 overflow-y-auto flex-1">
 {isPending ? (
 <InlineLoader message="Loading call log…" />
 ) : !data ? (
 <p className="text-sm text-gray-500">
 Call not found (it may have aged out of the 30-day gateway log retention).
 </p>
 ) : (
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-x-4 gap-y-3">
 <Field label="Time" value={data.TimeGenerated ? new Date(data.TimeGenerated).toLocaleString() : '—'} />
 <Field label="API" value={data.ApiId} />
 <Field label="Operation" value={data.OperationName} />
 <Field label="Product" value={data.ProductId} />
 <Field label="Subscription" value={data.ApimSubscriptionId} />
 <Field label="Method" value={data.Method} />
 <Field label="Status" value={`${data.ResponseCode}${data.BackendResponseCode != null ? ` (backend ${data.BackendResponseCode})` : ''}`} />
 <Field label="Latency" value={`${data.TotalTime || 0}ms`} />
 <div className="min-w-0">
 <div className="flex items-center gap-2">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Correlation Id</div>
 {data.CorrelationId && <CopyButton text={data.CorrelationId} label="" />}
 </div>
 <div className="text-sm text-gray-800 break-words">{data.CorrelationId ?? '—'}</div>
 </div>
 </div>
 <Field label="URL" value={data.Url} />
 {data.RequestBody && (
 <div>
 <div className="flex items-center justify-between mb-1">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Request body</div>
 <CopyButton text={data.RequestBody} />
 </div>
 <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-48">
 {typeof data.RequestBody === 'string' ? data.RequestBody : JSON.stringify(data.RequestBody, null, 2)}
 </pre>
 </div>
 )}
 {(data.ResponseBody || data.BackendResponseBody) && (
 <div>
 <div className="flex items-center justify-between mb-1">
 <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Response body</div>
 <CopyButton text={data.ResponseBody || data.BackendResponseBody} />
 </div>
 <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words max-h-48">
 {(() => { const b = data.ResponseBody || data.BackendResponseBody; return typeof b === 'string' ? b : JSON.stringify(b, null, 2); })()}
 </pre>
 </div>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 );
}
